import {
  forwardRef,
  ChangeEvent,
  ComponentPropsWithoutRef,
  ReactElement,
  useId,
} from "react";
import cx from "classnames";
import { Icon } from "../Icon";
import styles from "./Switch.module.scss";
import Check from "@servicetitan/hammer-icon/mdi/round/check.svg";
import { LayoutUtilProps } from "../../types";
import { useLayoutPropsUtil } from "../../hooks";
import { Helper } from "../../internal/components/Helper";
import { Flex } from "../Flex";

/**
 * State object returned by the Switch onChange callback
 */
export type SwitchState = {
  /**
   * Whether the switch is currently checked
   */
  checked: boolean;
  /**
   * The value of the switch input
   */
  value?: string | number | readonly string[];
};

/**
 * Props for the Switch component
 * @extends ComponentPropsWithoutRef<"input">
 * @extends LayoutUtilProps
 */
export type SwitchProps = Omit<ComponentPropsWithoutRef<"input">, "onChange"> &
  LayoutUtilProps & {
    /**
     * Callback when switch is changed.
     * @param e Change event object
     * @param state Current switch state with checked status and value
     */
    onChange?: (e: ChangeEvent<HTMLInputElement>, state: SwitchState) => void;

    /**
     * Label for the switch.
     * @remarks This should either be a string or have text content inside for accessibility
     */
    label?: ReactElement | string;

    /**
     * Description text to display below the label
     */
    description?: string;
  };

/**
 * Switch component for toggling between on/off states.
 *
 * Features:
 * - Toggle switch with smooth animations
 * - Check icon indicator when active
 * - Accessible with proper ARIA roles and attributes
 * - Keyboard navigation support (Space/Enter)
 * - Controlled and uncontrolled state management
 * - Customizable label and styling
 * - Layout utilities for positioning and spacing
 * - Focus management with visible focus indicators
 * - Hover states and visual feedback
 * - Disabled state support
 *
 * @example
 * <Switch
 *   label="Enable notifications"
 *   onChange={(e, state) => console.log('Switch toggled:', state.checked)}
 * />
 */
export const Switch = forwardRef<HTMLInputElement, SwitchProps>(
  (props, ref) => {
    const { layoutStyles, componentProps } = useLayoutPropsUtil(props);
    const {
      label,
      description,
      className,
      onChange,
      checked,
      defaultChecked,
      value,
      style,
      ...rest
    } = componentProps;

    const helperUid = useId();

    const onChangeHandler = (e: ChangeEvent<HTMLInputElement>) => {
      onChange?.(e, { value, checked: e.target.checked });
    };

    const switchClassNames = cx(styles["switch"], className);
    const styleCombined = {
      ...style,
      ...layoutStyles,
      alignItems: description ? "flex-start" : "center",
    };

    return (
      <label className={switchClassNames} style={styleCombined}>
        <input
          data-anv="switch"
          ref={ref}
          type="checkbox"
          role="switch"
          value={value}
          aria-checked={checked ?? defaultChecked}
          checked={checked}
          defaultChecked={defaultChecked}
          onChange={onChangeHandler}
          {...rest}
        />
        <span aria-hidden className={styles["toggle"]}>
          <span className={styles["indicator"]}>
            <Icon svg={Check} className={styles["check"]} />
          </span>
        </span>
        {label || description ? (
          <Flex direction="column" gap={1}>
            {label && label}
            {description && <Helper id={helperUid} description={description} />}
          </Flex>
        ) : null}
      </label>
    );
  },
);

Switch.displayName = "Switch";
